import { FC, useState } from "react";
import { useIntl } from "react-intl";

import Card from "components/v2/Card/Card";
import { GetCostDetailsResponseType } from "api/response";
import usePermissions from "hooks/usePermissions";
import Amount from "components/v2/Amount/Amount";
import Badge from "components/v2/Badge/Badge";
import Divider from "components/v2/Divider/Divider";
import { ReactComponent as PlusIcon } from "assets/images/iconsV2/plus.svg";
import { ReactComponent as TrashIcon } from "assets/images/iconsV2/trash-01.svg";
import Button from "components/v2/Button/Button";
import AdditionalCostDialogV2 from "pages/provider/ShipmentTracking/components/AdditionalCostDialog/AdditionalCostDialogV2";
import ConfirmRemoveCostDialog from "pages/shipper/ShipmentTracking/components/CostDetails/components/ConfirmRemoveCostDialog";
import { CostDetailCharge } from "api/models";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import EmptyState from "components/v2/EmptyState/EmptyState";
import { ReactComponent as FeaturedIcon } from "assets/images/iconsV2/featured-icon-gray.svg";

type CostDetailsProps = {
  /**
   * The shipment data.
   */
  shipment: {
    financial: {
      total_quote_amount_lsp: number;
      currency_code_lsp: string;
      total_quoted_amount: number;
      total_quoted_amount_currency: string;
      in_transit_cost: number;
      in_transit_cost_currency: string;
      expected_amount_due: number;
      expected_amount_due_currency: string;
      post_booking_in_transit_costs_data?: GetCostDetailsResponseType["costs"]["post_booking_in_transit_costs_data"];
    };
    settings: {
      allow_in_transit_cost_approval: boolean;
    };
  };
  /**
   * Whether the component is loading.
   */
  isLoading: boolean;
};

const CostDetails: FC<CostDetailsProps> = function ({ shipment, isLoading }) {
  const intl = useIntl();
  const { permissions } = usePermissions();
  const { user_type } = JSON.parse(permissions || "{}");

  const {
    total_quote_amount_lsp,
    total_quoted_amount,
    total_quoted_amount_currency,
    currency_code_lsp,
    post_booking_in_transit_costs_data,
    expected_amount_due,
    expected_amount_due_currency,
  } = shipment.financial ?? {};
  const { allow_in_transit_cost_approval = false } = shipment.settings ?? {};

  const quotedCost = user_type === "provider" ? total_quote_amount_lsp : total_quoted_amount;
  const quotedCostCurrency = user_type === "provider" ? currency_code_lsp : total_quoted_amount_currency;

  const postBookingCharges = [
    ...(post_booking_in_transit_costs_data?.["Origin Charges"] ?? []),
    ...(post_booking_in_transit_costs_data?.Freight ?? []),
    ...(post_booking_in_transit_costs_data?.["Destination Charges"] ?? []),
  ];
  const isPendingApproval = postBookingCharges.some((charge: any) => !charge.approved_by_shipper);

  const [isCostDialogOpen, setIsCostDialogOpen] = useState(false);
  const [isRemoveCostDialogOpen, setIsRemoveCostDialogOpen] = useState(false);
  const [selectedCost, setSelectedCost] = useState<CostDetailCharge>();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const handleRemoveCostClick = (cost: CostDetailCharge) => {
    setSelectedCost(cost);
    setIsRemoveCostDialogOpen(true);
  };

  const handleOpenCostDialog = () => {
    setIsCostDialogOpen(true);
  };

  if (isLoading)
    return (
      <div className="mb-3xl">
        <Card borderRadius="md">
          <Card.Header
            title={intl.formatMessage({ id: "tracking.costDetails", defaultMessage: "Cost details" })}
            showBorder
          />
          <LoadingSkeleton rows={1} />
        </Card>
      </div>
    );
  return (
    <>
      <AdditionalCostDialogV2
        isOpen={isCostDialogOpen}
        close={() => setIsCostDialogOpen(false)}
        expectedAmountDueCurrency={expected_amount_due_currency}
      />
      {selectedCost && (
        <ConfirmRemoveCostDialog
          isOpen={isRemoveCostDialogOpen}
          close={() => setIsRemoveCostDialogOpen(false)}
          selectedCostChild={selectedCost}
        />
      )}
      <div>
        <Card borderRadius="md">
          <Card.Header
            title={intl.formatMessage({ id: "tracking.costDetails", defaultMessage: "Cost details" })}
            actions={
              user_type === "provider" && (
                <Button
                  variant="link-color"
                  label={intl.formatMessage({ id: "tracking.addCost", defaultMessage: "Add cost" })}
                  iconLeft={<PlusIcon />}
                  onClick={handleOpenCostDialog}
                  className="mt-xs"
                />
              )
            }
            showBorder
          />
          {!isLoading && Object.keys(shipment).length === 0 && (
            <Card.Body>
              <EmptyState
                noActions
                customIcon={<FeaturedIcon />}
                title={intl.formatMessage({
                  id: "general.couldNotLoadDetails",
                  defaultMessage: "We couldn't load these details",
                })}
                description={intl.formatMessage({
                  id: "general.tryAgainOrCheckBackLater",
                  defaultMessage: "You can try again or check back later.",
                })}
              />
            </Card.Body>
          )}
          {!isLoading && Object.keys(shipment).length > 0 && (
            <>
              <Card.LineItem
                label={intl.formatMessage({ id: "quotation-form.totalCost", defaultMessage: "Total cost" })}
                rightContent={
                  <div className="d-flex align-items-center">
                    {isPendingApproval && (
                      <span className="mr-lg">
                        <Badge
                          type="status-badge"
                          color="warning"
                          label={intl.formatMessage({
                            id: "general.pendingApproval",
                            defaultMessage: "Pending approval",
                          })}
                        />
                      </span>
                    )}
                    <Amount amount={expected_amount_due} currency={expected_amount_due_currency} size="sm" />
                  </div>
                }
              />
              <Card.Accordion
                accordionTitle={(isOpen) => isOpen
                    ? intl.formatMessage({ id: "tracking.hideCostBreakdown", defaultMessage: "Hide cost breakdown" })
                    : intl.formatMessage({ id: "tracking.showCostBreakdown", defaultMessage: "Show cost breakdown" })}
              >
                <div className="pl-3xl">
                  <Divider />
                </div>
                <Card.LineItem
                  label={intl.formatMessage({ id: "invoice.quotedCost", defaultMessage: "Quoted cost" })}
                  rightContent={
                    <div className="d-flex align-items-center">
                      <Amount amount={quotedCost} currency={quotedCostCurrency} size="sm" />
                    </div>
                  }
                />
                {postBookingCharges?.map((charge: any, index: number) => {
                  const { amount_origin, currency_origin, description, cost_type, approved_by_shipper } = charge;
                  const amount = cost_type === 2 ? -amount_origin : amount_origin;

                  return (
                    <div key={index} onMouseEnter={() => setHoveredIndex(index)} onMouseLeave={() => setHoveredIndex(null)}>
                      <div className="pl-3xl">
                        <Divider />
                      </div>
                      <Card.LineItem
                        label={description}
                        rightContent={
                          <div className="d-flex align-items-center ">
                            {allow_in_transit_cost_approval && (
                              <div className="mr-lg">
                                {approved_by_shipper ? (
                                  <Badge
                                    type="status-badge"
                                    color="success"
                                    label={intl.formatMessage({ id: "general.approved", defaultMessage: "Approved" })}
                                  />
                                ) : (
                                  <Badge
                                    type="status-badge"
                                    color="warning"
                                    label={intl.formatMessage({
                                      id: "general.pendingApproval",
                                      defaultMessage: "Pending approval",
                                    })}
                                  />
                                )}
                              </div>
                            )}
                            <Amount amount={amount} currency={currency_origin} size="sm" />
                            {hoveredIndex === index && user_type === "provider" && (
                              <TrashIcon onClick={() => handleRemoveCostClick(charge)} className="ml-md cursor-pointer" />
                            )}
                          </div>
                        }
                      />
                    </div>
                  );
                })}
              </Card.Accordion>
            </>
          )}
        </Card>
      </div>
    </>
  );
};

export default CostDetails;
